import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Search, Bell, ChevronRight, ChevronDown, Check, LogOut, UserCircle, Menu, X, Building2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { UserAvatar } from "./UserAvatar";
import api from "../lib/api";

/* La barra in alto mostra le ETICHETTE del menù, in italiano: mai i nomi
   tecnici delle rotte. Un segmento sconosciuto che sembra un id (uuid, slug
   lungo) diventa "Dettaglio". */
const ROUTE_LABELS = {
  dashboard: "Dashboard",
  history: "Storico richieste",
  profile: "Profilo",
  search: "Catalogo",
  service: "Servizio",
  documents: "Documenti",
  attendance: "Presenze",
  messages: "Messaggi interni",
  support: "Supporto",
  checklist: "Checklist",
  formazione: "Formazione",
  manuale: "Manuale",
  billing: "Abbonamento",
  energia: "Energia",
  mercato: "Mercato",
  punti: "Punti",
  flussi: "Flussi",
  admin: "Amministrazione",
  users: "Utenti",
  services: "Servizi",
  logs: "Log attività",
  analytics: "Analytics",
  storage: "Storage PDF",
  ai: "Uso dell'AI",
  vies: "Verifica VIES",
  company: "Cerca Azienda",
  visure: "Visure Camerali",
  catasto: "Visure Catastali",
  "catasto-documenti": "Documenti Catastali Ufficiali",
  bilancio: "Bilancio Aziendale",
  assistant: "AI aziendale",
  reconcile: "Riconciliazione",
  "ask-file": "Chiedi al file",
  "analyze-doc": "Analizza documento",
  quality: "Controllo qualità",
  drafts: "Bozze",
  "ai-config": "Assistente AI",
  "ai-costs": "Costi AI",
  errori: "Registro errori",
  meetings: "Riunioni",
  superadmin: "Super Admin",
  organizations: "Organizzazioni",
  "tenant-features": "Funzioni per cliente",
  discovery: "Discovery",
  confronto: "Confronto",
  revenue: "Ricavi",
  subscriptions: "Abbonamenti",
  requests: "Tutte le richieste",
  pricing: "Piani e prezzi",
  costs: "Costi OpenAPI",
};

const segLabel = (s) => {
  if (ROUTE_LABELS[s]) return ROUTE_LABELS[s];
  if (/^[0-9a-f]{8}-[0-9a-f]{4}/i.test(s) || /^\d+$/.test(s) || s.length > 20) return "Dettaglio";
  const parola = s.replace(/-/g, " ");
  return parola.charAt(0).toUpperCase() + parola.slice(1);
};

/* Selettore org per amministratori di gruppo: sempre visibile in alto. */
const WorkspaceSwitcher = ({ user }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const current = user.workspaces.find((w) => w.id === user.active_org_id);

  const switchOrg = async (orgId) => {
    if (orgId === user.active_org_id || busy) return;
    setBusy(true);
    try {
      await api.put("/me/active-org", { org_id: orgId });
      window.location.href = "/dashboard";
    } catch {
      setBusy(false);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid="workspace-selector"
        className="flex items-center gap-2 h-9 px-3 rounded-md border border-border bg-surface text-sm hover:bg-subtle transition-colors"
      >
        <Building2 size={14} strokeWidth={1.5} className="text-accent" />
        <span className="hidden md:inline text-[11px] text-text-muted">Stai lavorando in:</span>
        <span className="font-semibold text-text-primary max-w-[140px] truncate" data-testid="workspace-current">
          {current?.name || user.active_org_id || "—"}
        </span>
        <ChevronDown size={14} strokeWidth={1.5} className="text-text-muted" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-64 bg-surface border border-border rounded-md shadow-lg z-50 py-1" data-testid="workspace-menu">
          <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-text-muted">Le tue organizzazioni</div>
          {user.workspaces.map((w) => (
            <button
              key={w.id}
              type="button"
              onClick={() => switchOrg(w.id)}
              data-testid={`workspace-option-${w.id}`}
              className={`w-full flex items-center justify-between text-left px-3 py-2 text-sm hover:bg-subtle ${
                w.id === user.active_org_id ? "font-semibold text-text-primary" : "text-text-secondary"
              }`}
            >
              <span className="truncate">{w.name}</span>
              {w.id === user.active_org_id && <Check size={14} strokeWidth={2} className="text-accent shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const Topbar = ({ onMenuToggle, mobileNavOpen = false }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const parts = location.pathname.split("/").filter(Boolean);

  const onLogout = async () => { await logout(); navigate("/login"); };

  return (
    <header className="wl-topbar">
      {/* Hamburger — mobile only */}
      {onMenuToggle && (
        <button
          type="button"
          onClick={onMenuToggle}
          data-testid="mobile-menu-toggle"
          aria-label={mobileNavOpen ? "Chiudi menu" : "Apri menu"}
          aria-expanded={mobileNavOpen}
          className="wl-topbar__hamburger"
        >
          {mobileNavOpen ? <X size={20} strokeWidth={1.5} /> : <Menu size={20} strokeWidth={1.5} />}
        </button>
      )}

      {/* Breadcrumb / titolo */}
      <nav className="wl-topbar__breadcrumb" aria-label="breadcrumb">
        {parts.length === 0 ? (
          <span className="wl-topbar__breadcrumb-current">Dashboard</span>
        ) : parts.map((p, i) => {
          const last = i === parts.length - 1;
          return (
            <React.Fragment key={i}>
              {i > 0 && <ChevronRight size={14} strokeWidth={1.5} className="wl-topbar__breadcrumb-sep" />}
              <span className={last ? "wl-topbar__breadcrumb-current" : "wl-topbar__breadcrumb-item"}>
                {segLabel(p)}
              </span>
            </React.Fragment>
          );
        })}
      </nav>

      {/* Actions */}
      <div className="wl-topbar__actions">
        {user?.is_group_admin && user?.workspaces?.length > 0 && (
          <WorkspaceSwitcher user={user} />
        )}
        <button
          type="button"
          data-testid="topbar-search"
          aria-label="Cerca"
          className="wl-topbar__icon-btn"
        >
          <Search size={18} strokeWidth={1.5} />
        </button>
        <button
          type="button"
          data-testid="topbar-bell"
          aria-label="Notifiche"
          className="wl-topbar__icon-btn"
        >
          <Bell size={18} strokeWidth={1.5} />
        </button>

        <div className="relative" ref={ref}>
          <button
            type="button"
            onClick={() => setOpen(!open)}
            data-testid="topbar-avatar"
            className="wl-topbar__avatar"
            aria-label="Menu profilo"
          >
            <UserAvatar userId={user?.id} name={user?.full_name || user?.email} version={user?.avatar_version} size={30} />
          </button>
          {open && (
            <div className="wl-topbar__menu" data-testid="avatar-menu">
              <div className="wl-topbar__menu-header">
                <div className="wl-topbar__menu-email">{user?.email}</div>
                <div className="wl-topbar__menu-role">
                  {user?.role === "super_admin"
                    ? "Super Admin"
                    : user?.role === "admin"
                    ? "Amministratore"
                    : "Operatore"}
                </div>
              </div>
              <Link to="/profile" onClick={() => setOpen(false)} className="wl-topbar__menu-item">
                <UserCircle size={16} strokeWidth={1.5} /> Profilo
              </Link>
              <button
                onClick={onLogout}
                data-testid="logout-button"
                className="wl-topbar__menu-item wl-topbar__menu-item--danger"
              >
                <LogOut size={16} strokeWidth={1.5} /> Esci
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
